import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ChevronDownIcon } from '../../icons';
import { cx, sortCx } from '../../utils/cx';

const styles = sortCx({
  root: 'group relative flex w-full cursor-pointer items-center rounded-md outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
  rootSelected: 'bg-active hover:bg-secondary_hover',
});

interface INavItemBaseProps {
  /** Whether the nav item shows only an icon. */
  iconOnly?: boolean;
  /** URL to navigate to when the nav item is clicked. */
  href?: string;
  /** Type of the nav item. */
  type: 'link' | 'collapsible' | 'collapsible-child';
  /** Icon component to display. */
  icon?: React.FC<React.HTMLAttributes<HTMLOrSVGElement>>;
  /** Whether the nav item is currently active. */
  current?: boolean;
  /** Whether to truncate the label text. */
  truncate?: boolean;
  /** Handler for click events. */
  onClick?: React.MouseEventHandler;
  /** Content to display. */
  children?: React.ReactNode;
}

const NavItem = ({ current, type, href, icon: Icon, children, truncate = true, onClick }: INavItemBaseProps) => {
  const iconElement = Icon && (
    <Icon
      aria-hidden="true"
      className={cx(
        'text-fg-quaternary transition-inherit-all mr-2 size-5 shrink-0',
        current && 'text-fg-brand-primary'
      )}
    />
  );

  const labelElement = (
    <span
      className={cx(
        'text-md text-secondary transition-inherit-all group-hover:text-secondary_hover flex-1 font-semibold',
        truncate && 'truncate',
        current && 'text-secondary_hover'
      )}
    >
      {children}
    </span>
  );

  if (type === 'collapsible') {
    return (
      <summary className={cx('px-3 py-2', styles.root, current && styles.rootSelected)} onClick={onClick}>
        {iconElement}

        {labelElement}

        <ChevronDownIcon
          aria-hidden="true"
          className="text-fg-quaternary in-open:-scale-y-100 ml-3 size-4 shrink-0 stroke-[2.5px]"
        />
      </summary>
    );
  }

  if (type === 'collapsible-child') {
    return (
      <RouterLink
        to={href || '#'}
        className={cx('py-2 pl-10 pr-3', styles.root, current && styles.rootSelected)}
        onClick={onClick}
        aria-current={current ? 'page' : undefined}
      >
        {labelElement}
      </RouterLink>
    );
  }

  return (
    <RouterLink
      to={href || '#'}
      className={cx('px-3 py-2', styles.root, current && styles.rootSelected)}
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
    >
      {iconElement}
      {labelElement}
    </RouterLink>
  );
};

export default NavItem;
